import React, { useCallback, useState } from 'react'

import { useEvent } from '@emerald-works/react-event-bus-client'
import { Box, Chip, LinearProgress, Skeleton, Typography, useTheme } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { ActionButton } from '../../../../components'
import { useAlert } from '../../../../hooks'
import { ScormPlayer } from '../scorm-player/scorm-player'
import SkillBuilderCardContainer from './skill-builder-card-container'
import { SkillBuilderCardInfoContainer } from './skill-builder-card-info-container'

export const useStyles = makeStyles(
  ({ palette, typography, spacing, breakpoints, props }) => ({
    cardContent: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      gap: spacing(2.5)
    },
    image: {
      borderRadius: spacing(1),
      width: '100%',
      height: '100%',
      aspectRatio: 16 / 9,
      objectFit: 'cover'
    },
    actionsContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing(2),
      width: '100%'
    },
    progressContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing(1),
      flex: 1
    },
    progressBar: {
      flex: 1,
      height: 6,
      borderRadius: 3,
      backgroundColor: palette.grey.shade[20],
      '& .MuiLinearProgress-bar': {
        borderRadius: 3
      }
    },
    progressText: {
      ...typography.body.s,
      color: palette.text.secondary,
      minWidth: 35,
      textAlign: 'right'
    }
  })
)

const SkillBuilderCard = ({
  skillBuilderLearningItem,
  isLoading,
  isLoadingProgress,
  onLaunchCourse = () => { },
  onRefetchMSBCourseProgress = () => { },
  ...props
}) => {
  const classes = useStyles(props)
  const theme = useTheme()
  const { palette } = theme
  const { enqueueMsg } = useAlert()
  const [openScormPlayer, setOpenScormPlayer] = useState(false)
  const [scormLaunchLink, setScormLaunchLink] = useState(null)
  const hasStarted = skillBuilderLearningItem?.userProgress?.status !== 'not_started'

  const [getScormLaunchLink] = useEvent([{
    eventName: 'scormGetAccessLink',
    onSuccess: (payload) => {
      console.log('SCORM launch success:', payload)
      setScormLaunchLink(payload.launchLink)
      setOpenScormPlayer(true)
    },
    onError: (err) => {
      console.error('SCORM launch error:', err)
      enqueueMsg('An unexpected error happened, please try again later', 'error')
    }
  }])

  const handleLaunchCourse = () => {
    console.log('Launching course:', {
      courseId: skillBuilderLearningItem?.id,
      fullItem: skillBuilderLearningItem
    })

    if (!skillBuilderLearningItem?.id) {
      console.error('Course ID is missing:', skillBuilderLearningItem)
      enqueueMsg('Unable to launch course: Course ID is missing', 'error')
      return
    }

    getScormLaunchLink.trigger({ courseId: skillBuilderLearningItem.id })
    onLaunchCourse && onLaunchCourse(skillBuilderLearningItem)
  }

  const renderCardImage = useCallback(() => {
    if (isLoading) {
      return (
        <Skeleton className={classes.image} />
      )
    } else {
      return (
        <Box>
          <img
            width='100%'
            height='100%'
            className={classes.image}
            src={skillBuilderLearningItem.image.imgUrl}
            alt={skillBuilderLearningItem.image.altText || skillBuilderLearningItem?.title || 'Course thumbnail'}
            data-test={`skill-builder-card-image-${skillBuilderLearningItem?.title}`}
          />
        </Box>
      )
    }
  }, [isLoading, classes, skillBuilderLearningItem])

  function renderCardActions () {
    if (isLoading || isLoadingProgress) {
      return (
        <Box className={classes.actionsContainer}>
          <Skeleton height={40} width={120} />
          <Skeleton height={6} sx={{ flex: 1 }} />
        </Box>
      )
    }

    if (skillBuilderLearningItem?.userProgress?.status === 'completed') {
      return (
        <Box className={classes.actionsContainer}>
          <Chip
            color='success'
            label='Completed'
            sx={{
              borderRadius: '4px',
              backgroundColor: palette.success.main,
              color: palette.common.white,
              height: '36px',
              '& .MuiChip-label': {
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }
            }}
            // icon={<CheckCircleOutline />}
            data-test={`skill-builder-card-status-${skillBuilderLearningItem?.id}`}
            aria-label='Course completed'
          />
        </Box>
      )
    }

    return (
      <Box className={classes.actionsContainer}>
        <ActionButton
          variant='neutral'
          label={hasStarted ? 'Resume Course' : 'Start Course'}
          loading={getScormLaunchLink.isWorking}
          onClick={handleLaunchCourse}
          data-test={`skill-builder-card-see-detail-button-${skillBuilderLearningItem?.title}`}
          sx={{ flexShrink: 0 }}
        />
        {skillBuilderLearningItem?.userProgress?.status !== 'not_started' &&
          (
            <Box className={classes.progressContainer}>
              <LinearProgress
                variant='determinate'
                value={skillBuilderLearningItem?.userProgress?.progress}
                className={classes.progressBar}
                sx={{
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: palette.text.secondary
                  }
                }}
                aria-label={`Course progress: ${skillBuilderLearningItem?.userProgress?.progress} percent`}
                aria-valuenow={skillBuilderLearningItem?.userProgress?.progress}
                aria-valuemin={0}
                aria-valuemax={100}
                role='progressbar'
                data-test={`skill-builder-card-progress-${skillBuilderLearningItem?.id}`}
              />
              <Typography
                className={classes.progressText}
                data-test={`skill-builder-card-progress-text-${skillBuilderLearningItem?.id}`}
                aria-label={`${skillBuilderLearningItem?.userProgress?.progress} percent complete`}
              >
                {skillBuilderLearningItem?.userProgress?.progress}%
              </Typography>
            </Box>
          )}
      </Box>
    )
  }

  return (
    <>
      <ScormPlayer
        open={openScormPlayer}
        onClose={() => {
          setOpenScormPlayer(false)
          onRefetchMSBCourseProgress(skillBuilderLearningItem?.id)
        }}
        url={scormLaunchLink}
        title={skillBuilderLearningItem?.title}
      />
      <SkillBuilderCardContainer dataTest={`skill-builder-card-${skillBuilderLearningItem?.id}`}>
        <Box className={classes.cardContent}>
          <Box>
            {renderCardImage()}
          </Box>
          <Box display='flex' flex={1}>
            <SkillBuilderCardInfoContainer isLoading={isLoading} name={skillBuilderLearningItem?.title} description={skillBuilderLearningItem?.description} />
          </Box>
        </Box>
        <Box mt={1}>
          {renderCardActions()}
        </Box>
      </SkillBuilderCardContainer>
    </>
  )
}

export default SkillBuilderCard
